import { SEAT_CLASS_TYPE_LIST } from '@/constants'
import dayjs from 'dayjs'

/** 含上/中/下铺概念的席别 code（与 constants 一致） */
export const BERTH_SEAT_TYPE_CODES = new Set([7, 5, 6, 4, 9, 10])

/** 三档铺位：硬卧、二等卧 */
export const THREE_LEVEL_BERTH_CODES = new Set([7, 5])

/**
 * 是否卧铺类席别
 * @param {number} seatType
 */
export function isBerthSeatType(seatType) {
  return seatType != null && BERTH_SEAT_TYPE_CODES.has(Number(seatType))
}

/**
 * 铺位层级文案：优先接口字段，其次从 seatNumber 中识别「上铺/中铺/下铺」
 * @param {Record<string, unknown>} row 乘车人一行（含 seatType、carriageNumber、seatNumber、berthLevel 等）
 */
export function getBerthLevelLabel(row) {
  if (!row) return ''
  if (row.berthPosition) return String(row.berthPosition)
  const lv = row.berthLevel ?? row.berthType
  if (lv === 1 || lv === '1' || lv === 'UPPER') return '上铺'
  if (lv === 2 || lv === '2' || lv === 'MIDDLE') return '中铺'
  if (lv === 3 || lv === '3' || lv === 'LOWER') return '下铺'
  const sn = String(row.seatNumber ?? '')
  if (sn.includes('上')) return '上铺'
  if (sn.includes('中')) return '中铺'
  if (sn.includes('下')) return '下铺'
  return ''
}

/**
 * 席位第二行：卧铺为「xx车yy号上铺」；非卧铺为「xx车yy号」
 */
export function formatSeatSecondLine(row) {
  if (!row) return ''
  const car = row.carriageNumber ?? ''
  const num = String(row.seatNumber ?? '').replace(/上铺|中铺|下铺/g, '')
  const berth = getBerthLevelLabel(row)
  if (isBerthSeatType(row.seatType) && berth) {
    return `${car}车厢${num}号${berth}`
  }
  if (isBerthSeatType(row.seatType)) {
    return `${car}车厢${num}号`
  }
  return `${car}车${row.seatNumber ?? ''}号`
}

/** 折扣展示：discountRate 0.65 -> 6.5折 */
export function formatDiscountTag(row) {
  const r = row?.discountRate ?? row?.discount
  if (r == null || r === '' || Number(r) >= 1) return ''
  const n = Number(r)
  if (Number.isNaN(n) || n <= 0) return ''
  const zhe = Math.round(n * 100) / 10
  return `${zhe}折`
}

/**
 * 业务流水号：优先接口，否则按订单+乘车人生成占位（便于联调前展示）
 */
export function getRefundSerial(row) {
  return (
    row?.refundSerialNo ||
    row?.businessSerialNo ||
    row?.tradeSerialNo ||
    (row?.orderSn && row?.id != null
      ? `R${String(row.orderSn).slice(-6)}${String(row.id).slice(-4)}`
      : '')
  )
}

/** 出发日期与时间，如「 2026-06-15 08:00」 */
export function formatDepartureScheduleText(ridingDate, departureTime) {
  const dateStr = ridingDate ? dayjs(ridingDate).format('YYYY-MM-DD') : ''
  let timeStr = ''
  if (departureTime != null && departureTime !== '') {
    const raw = String(departureTime)
    if (/^\d{1,2}:\d{2}/.test(raw)) {
      timeStr = raw.slice(0, 5)
    } else {
      const t = dayjs(departureTime)
      if (t.isValid()) {
        timeStr = t.format('HH:mm')
      }
    }
  }
  if (!dateStr && !timeStr) return ''
  if (dateStr && timeStr) return ` ${dateStr} ${timeStr}`
  return ` ${dateStr || timeStr}`
}

/** 时间轴摘要文案 */
export function formatTimelineSeatSnippet(
  row,
  trainNumber,
  departure,
  arrival,
  ridingDate,
  departureTime
) {
  const seatName =
    SEAT_CLASS_TYPE_LIST.find((s) => s.code === row?.seatType)?.label ?? ''
  const pos = formatSeatSecondLine(row)
  const schedule = formatDepartureScheduleText(ridingDate, departureTime)
  return `${row?.realName ?? ''} ${trainNumber ?? ''} (${seatName} ${pos}) ${departure ?? ''}-${arrival ?? ''}${schedule}`
}

/** 为已有流水明细补全出发日期时间（兼容历史数据） */
export function enrichTimelineDetailLine(line, order) {
  if (!line || !order) return line

  const schedule = formatDepartureScheduleText(order.ridingDate, order.departureTime)
  const route = `${order.departure ?? ''}-${order.arrival ?? ''}`

  if (schedule && route !== '-' && line.includes(route)) {
    const idx = line.lastIndexOf(route)
    const afterRoute = line.slice(idx + route.length)
    if (!/^\s+\d{4}-\d{2}-\d{2}/.test(afterRoute)) {
      return line + schedule
    }
    return line
  }

  const rescheduleApplyMatch = line.match(/^(.+?)\s+申请改签/)
  if (rescheduleApplyMatch && order.passengerDetails?.length) {
    const name = rescheduleApplyMatch[1].trim()
    const passenger = order.passengerDetails.find((p) => p.realName === name)
    if (passenger && route !== '-' && !line.includes(route)) {
      const snippet = formatTimelineSeatSnippet(
        passenger,
        order.trainNumber,
        order.departure,
        order.arrival,
        order.ridingDate,
        order.departureTime
      )
      return line.replace(`${name} 申请改签`, `${snippet} 申请改签`)
    }
  }

  if (schedule && route !== '-' && line.includes('车次') && line.includes(route)) {
    const idx = line.lastIndexOf(route)
    const afterRoute = line.slice(idx + route.length)
    if (!/^\s+\d{4}-\d{2}-\d{2}/.test(afterRoute)) {
      return line + schedule
    }
  }

  return line
}

/**
 * 从订单状态流水解析出票失败原因（与订单详情时间轴一致）
 * @param {Array<{ success?: boolean, eventTitle?: string, detailLines?: string[] }>} logs
 */
export function parseTicketFailReason(logs) {
  if (!Array.isArray(logs) || !logs.length) return ''
  const sorted = [...logs].sort((a, b) => {
    const ta = a?.eventTime ? new Date(a.eventTime).getTime() : 0
    const tb = b?.eventTime ? new Date(b.eventTime).getTime() : 0
    return tb - ta
  })
  for (const log of sorted) {
    if (log?.success !== false) continue
    const reasonLine = log.detailLines?.find((line) =>
      String(line ?? '').includes('失败原因')
    )
    if (reasonLine) {
      return String(reasonLine).replace(/^失败原因[：:]\s*/, '').trim()
    }
    if (log.eventTitle?.includes('出票失败')) {
      const extra = log.detailLines?.find(
        (line) => line && !String(line).includes('次') && !String(line).includes('站')
      )
      if (extra) return String(extra).trim()
    }
  }
  return ''
}
